import { useEffect, useMemo, useState } from "react";
import { ImagePlus, Save, XCircle } from "lucide-react";
import {
  getInitials,
  isGoogleDriveLink,
  resolveImageUrlCandidates
} from "../../utils/googleDriveImage";

const countryOptions = ["USA", "Canada", "UK", "Australia", "UAE", "New Zealand", "Europe", "Ireland", "Germany"];

function AdminTestimonialForm({
  formValues,
  onChange,
  onSubmit,
  isEditing,
  onCancel,
  submitError
}) {
  const [imageError, setImageError] = useState(false);
  const [candidateIndex, setCandidateIndex] = useState(0);

  const previewCandidates = useMemo(
    () => resolveImageUrlCandidates(formValues.studentPhoto),
    [formValues.studentPhoto]
  );
  const previewUrl = previewCandidates[candidateIndex] || "";
  const initials = getInitials(formValues.name || "Student");
  const showDriveAccessWarning =
    imageError && isGoogleDriveLink(formValues.studentPhoto) && formValues.studentPhoto.trim().length > 0;

  useEffect(() => {
    setImageError(false);
    setCandidateIndex(0);
  }, [formValues.studentPhoto]);

  useEffect(() => {
    if (candidateIndex >= previewCandidates.length && previewCandidates.length > 0) {
      setImageError(true);
    }
  }, [candidateIndex, previewCandidates.length]);

  const handleImageError = () => {
    setCandidateIndex((current) => {
      if (current + 1 < previewCandidates.length) {
        return current + 1;
      }
      return previewCandidates.length;
    });
  };

  const inputClass =
    "mt-2 w-full rounded-xl border border-midnight-navy/15 bg-white px-4 py-3 text-sm text-midnight-navy placeholder:text-slate-text/45 focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/25";

  return (
    <section className="rounded-2xl border border-midnight-navy/12 bg-white/92 p-6 shadow-soft">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-royal-blue">
            {isEditing ? "Edit Testimonial" : "Add Testimonial"}
          </p>
          <h2 className="mt-2 text-xl md:text-2xl">
            {isEditing ? "Update Student Story" : "Create New Student Story"}
          </h2>
        </div>

        {isEditing ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-lg border border-midnight-navy/15 px-3 py-1.5 text-xs font-semibold text-slate-text/80 hover:border-midnight-navy/30"
          >
            <XCircle className="h-3.5 w-3.5" />
            Cancel edit
          </button>
        ) : null}
      </div>

      <form className="mt-6 space-y-5" onSubmit={onSubmit} noValidate>
        <label className="block text-sm font-medium text-midnight-navy" htmlFor="studentPhoto">
          Student Photo Drive Link / Image URL
          <input
            id="studentPhoto"
            name="studentPhoto"
            type="url"
            value={formValues.studentPhoto}
            onChange={onChange}
            placeholder="https://drive.google.com/file/d/... or https://example.com/photo.jpg"
            className={inputClass}
          />
        </label>

        <div className="rounded-xl bg-sky-glow/70 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-text/70">
            Photo Preview
          </p>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 overflow-hidden rounded-full border border-midnight-navy/12 bg-white">
              {previewUrl && !imageError ? (
                <img
                  src={previewUrl}
                  alt="Student preview"
                  className="h-full w-full object-cover"
                  onError={handleImageError}
                />
              ) : (
                <span className="inline-flex h-full w-full items-center justify-center text-sm font-semibold text-midnight-navy">
                  {initials}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-text/80">
              Google Drive links are auto-converted where possible. If image fails, initials avatar is used.
            </p>
          </div>

          {showDriveAccessWarning ? (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Drive image could not be loaded. The file may be private. Set sharing to
              <span className="font-semibold"> “Anyone with the link can view” </span>
              and try again.
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm font-medium text-midnight-navy" htmlFor="name">
            Name
            <input
              id="name"
              name="name"
              type="text"
              value={formValues.name}
              onChange={onChange}
              className={inputClass}
              required
            />
          </label>

          <label className="block text-sm font-medium text-midnight-navy" htmlFor="country">
            Country
            <select
              id="country"
              name="country"
              value={formValues.country}
              onChange={onChange}
              className={inputClass}
              required
            >
              <option value="">Select country</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-sm font-medium text-midnight-navy" htmlFor="course">
          Course
          <input
            id="course"
            name="course"
            type="text"
            value={formValues.course}
            onChange={onChange}
            className={inputClass}
            required
          />
        </label>

        <label className="block text-sm font-medium text-midnight-navy" htmlFor="testimonial">
          What Student Said
          <textarea
            id="testimonial"
            name="testimonial"
            value={formValues.testimonial}
            onChange={onChange}
            className={`${inputClass} min-h-[130px] resize-y`}
            placeholder="Write the testimonial quote..."
            required
          />
        </label>

        {submitError ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>
        ) : null}

        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-midnight-navy px-5 py-3 text-sm font-semibold text-soft-white shadow-soft hover:bg-royal-blue"
        >
          {isEditing ? <Save className="h-4 w-4" aria-hidden="true" /> : <ImagePlus className="h-4 w-4" aria-hidden="true" />}
          {isEditing ? "Update Testimonial" : "Add Testimonial"}
        </button>
      </form>
    </section>
  );
}

export default AdminTestimonialForm;
